import { useApiMutation } from './useApiMutation';
import { useApiQuery } from './useApiQuery';
import { notificationService } from '../services/notificationService';
import type { CreateAdminNotificationPayload } from '../types/notification.types';
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { queryTags } from '../queryTags';
import type { PaginationParams, SearchParams } from '../types';
import { getQueryClient } from '../queryClient';

/**
 * Send admin notification hook
 */
export function useSendAdminNotification(
    options?: UseMutationOptions<any, any, CreateAdminNotificationPayload, any>
) {
    return useApiMutation<any, any, CreateAdminNotificationPayload, any>({
        mutationFn: ((payload: CreateAdminNotificationPayload) =>
            notificationService.sendAdminNotification(payload)) as any,
        ...options,
    });
}

/**
 * Get admin notifications list hook
 */
export function useGetAdminNotifications(
    params?: PaginationParams & SearchParams & { type?: number },
    options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
    return useApiQuery({
        queryKey: [queryTags.notification, 'admin-list', params?.page || 1, params?.limit || 10, params?.search || '', params?.type],
        queryFn: () => notificationService.getAdminNotifications(params),
        ...options,
    });
}

/**
 * Get single admin notification hook
 */
export function useGetAdminNotification(
    id: string,
    options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
    return useApiQuery({
        queryKey: [queryTags.notification, 'admin-detail', id],
        queryFn: () => notificationService.getAdminNotification(id),
        enabled: !!id,
        ...options,
    });
}

/**
 * Delete admin notification hook
 */
export function useDeleteAdminNotification(
    options?: UseMutationOptions<any, any, string, any>
) {
    return useApiMutation<any, any, string, any>({
        mutationFn: ((id: string) => notificationService.deleteAdminNotification(id)) as any,
        onSuccess: () => {
            getQueryClient().invalidateQueries({ queryKey: [queryTags.notification] });
        },
        ...options,
    });
}
